import { useQuery } from "@tanstack/react-query";
import type { DeliveryGrain, DeliverySeries } from "@aura/shared";
import { Figure } from "~/components/charts";
import { ed } from "~/components/editorial";
import { api } from "~/lib/api";
import { fmtInt, fmtPct } from "~/lib/format";
import { CheckMix, CheckShareLines, GroupLines, MedianLines, MergedColumns } from "./charts";
import { mergedTable, periodLabel } from "./series";
import { botsQuery, useBotsParam, useGrainParam } from "./lib";
import { BotsToggle, Byline, Controls, ReportShell, Segments } from "./parts";
import { reviewTrend } from "./story";

function useSeries(grain: DeliveryGrain, by: DeliverySeries["by"], includeBots: boolean) {
  return useQuery({
    queryKey: ["delivery-series", grain, by, includeBots],
    queryFn: () =>
      api.get<{ data: DeliverySeries }>(`/delivery/series?grain=${grain}&by=${by}${botsQuery(includeBots)}`),
    staleTime: 5 * 60 * 1000,
  });
}

const unitOf = (g: DeliveryGrain) => (g === "week" ? "week" : g === "month" ? "month" : "quarter");

// Every period from the start of stored history, by week, month or quarter.
export function TrendsPage() {
  const { includeBots, setIncludeBots } = useBotsParam();
  const [grain, setGrain] = useGrainParam("month");
  const pxe = useSeries(grain, "pxe", includeBots);
  const products = useSeries(grain, "product", includeBots);
  const orgs = useSeries(grain, "org", includeBots);
  const s = pxe.data?.data;
  const unit = unitOf(grain);

  // Busiest complete period, for the headline.
  const complete = s ? s.periods.filter((p) => !p.live) : [];
  const busiest = s
    ? complete
        .map((p) => ({ p, merged: s.points.find((x) => x.period === p.period)?.stats.merged ?? 0 }))
        .sort((a, b) => b.merged - a.merged)[0]
    : undefined;
  const total = s ? s.points.reduce((sum, x) => sum + x.stats.merged, 0) : 0;

  return (
    <ReportShell meta={s ? `Since ${s.periods[0]?.label ?? ""}` : undefined}>
      <Controls>
        <Segments
          label="Charts by"
          value={grain}
          onChange={setGrain}
          options={[
            { value: "week", label: "Week" },
            { value: "month", label: "Month" },
            { value: "quarter", label: "Quarter" },
          ]}
        />
        <BotsToggle includeBots={includeBots} onChange={setIncludeBots} />
      </Controls>

      {pxe.isLoading ? (
        <div className={ed.loading}>Loading trends…</div>
      ) : !s ? (
        <div className={ed.loading}>Couldn't load trends.</div>
      ) : (
        <>
          <div className={ed.kicker}>Engineering delivery · trends by {unit}</div>
          <h1 className={ed.headlineCompact}>
            {busiest && busiest.merged > 0
              ? `PxE merged ${fmtInt(total)} PRs since ${s.periods[0]?.label}; the busiest ${unit} was ${busiest.p.label} with ${fmtInt(busiest.merged)}`
              : "PxE trends"}
          </h1>
          {reviewTrend(s) && <p className={ed.dek}>{reviewTrend(s)}</p>}
          <Byline title="Trends" meta={s} />

          <div className={ed.sect}>I. Volume</div>
          <Figure
            title={`PxE PRs merged each ${unit}`}
            sub="Ink is checked before merge, gray merged with no check. The last column is in progress."
            method={`Calendar ${unit}s in UTC${grain === "week" ? ", Monday start" : ""}. An asterisk marks a period that starts before every org's history does.`}
            table={mergedTable(s)}
          >
            <MergedColumns series={s} />
          </Figure>

          <div className={ed.sect}>II. Checked before merge</div>
          <Figure
            title={`Share of PxE PRs checked and reviewed before merge, by ${unit}`}
            method="Checked: reviewed by someone other than the author before the merge, merged by a person other than the author, or a GitHub Actions run passed on the PR before the merge. Reviewed alone is the narrower measure pr_metrics.py uses."
            table={{
              columns: ["Period", "Checked %", "Reviewed", "Merged by other", "CI only", "Not checked", "Checks pending"],
              rows: s.periods.map((p) => {
                const st = s.points.find((x) => x.period === p.period)?.stats;
                const none = (st?.merged ?? 0) - (st?.checked ?? 0);
                return [
                  periodLabel(p, s.grain),
                  fmtPct(st?.checkedPct),
                  fmtInt(st?.viaReview ?? 0),
                  fmtInt(st?.viaMerger ?? 0),
                  fmtInt(st?.viaCi ?? 0),
                  fmtInt(none),
                  fmtInt(st?.checksUnknown ?? 0),
                ];
              }),
            }}
          >
            <CheckShareLines series={s} />
          </Figure>
          <Figure
            title="How each PR was checked"
            sub={grain === "week" ? "The last 26 weeks." : undefined}
            method="Each PR counts once, in this order: reviewed, then merged by someone else, then CI passed. Not checked means none of the three. PRs whose merged-by and CI are still being backfilled read as not checked."
          >
            <CheckMix series={s} />
          </Figure>

          <div className={ed.sect}>III. Speed</div>
          <Figure
            title={`Median hours from PR opened to first review and to merge, by ${unit}`}
            method="Medians over merged PxE PRs. First review counts only reviews by someone else before the merge; PRs never reviewed are left out of that line."
          >
            <MedianLines series={s} />
          </Figure>

          {products.data?.data && (
            <>
              <div className={ed.sect}>IV. By product</div>
              <Figure
                title={`Merged PRs by product, by ${unit}`}
                sub="The five largest products by volume; the rest add up to Other. Includes Marketplace."
                method="Products come from Admin, Products: the repo's override, else its name."
                table={{
                  columns: ["Product", ...products.data.data.periods.map((p) => periodLabel(p, grain))],
                  rows: products.data.data.groups.map((g) => [
                    g.label,
                    ...products.data!.data.periods.map((p) =>
                      fmtInt(products.data!.data.points.find((x) => x.period === p.period && x.key === g.key)?.stats.merged ?? 0),
                    ),
                  ]),
                }}
              >
                <GroupLines series={products.data.data} />
              </Figure>
            </>
          )}

          {orgs.data?.data && (
            <>
              <div className={ed.sect}>V. By org</div>
              <Figure title={`Merged PRs by GitHub org, by ${unit}`} method="Every repo in the org, Marketplace included.">
                <GroupLines series={orgs.data.data} />
              </Figure>
            </>
          )}
        </>
      )}
    </ReportShell>
  );
}
